const normalizeSourceValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const CHANNEL_MANAGER_SOURCE_MARKERS = [
  "channex",
  "channelmanager",
  "lighthouse",
  "logismanager",
  "bookingcom",
  "expedia",
  "airbnb",
  "agoda",
  "hotelscom",
];

export const RESERVATION_MODIFICATION_SOURCE_META = [
  {
    key: "pms",
    label: "PMS / Manual",
    description: "Reservations created directly by the hotel team in Pilcore.",
  },
  {
    key: "booking_engine",
    label: "Booking Engine",
    description: "Reservations created by guests through the Pilcore Booking Engine.",
  },
  {
    key: "channel_manager",
    label: "Channel Manager",
    description: "Reservations received from OTAs and external distribution partners.",
  },
];

export const RESERVATION_MODIFICATION_ACTION_META = [
  {
    key: "allow_cancellation",
    action: "cancellation",
    label: "Cancellation",
    description: "Allow the hotel team to cancel the reservation manually.",
  },
  {
    key: "allow_date_changes",
    action: "dates",
    label: "Stay dates",
    description: "Allow check-in, check-out and number-of-nights changes.",
  },
  {
    key: "allow_guest_changes",
    action: "guests",
    label: "Number of guests",
    description: "Allow changes to the number of adults, children and babies.",
  },
  {
    key: "allow_room_category_changes",
    action: "room_category",
    label: "Room category",
    description: "Allow upgrades, downgrades and other room-category changes.",
  },
  {
    key: "allow_rate_price_changes",
    action: "rate_prices",
    label: "Rate prices",
    description: "Allow rate, nightly price and price override changes.",
  },
];

export const DEFAULT_RESERVATION_MANUAL_MODIFICATION_POLICY = {
  pms: {
    allow_modifications: true,
    allow_cancellation: true,
    allow_date_changes: true,
    allow_guest_changes: true,
    allow_room_category_changes: true,
    allow_rate_price_changes: true,
  },
  booking_engine: {
    allow_modifications: true,
    allow_cancellation: true,
    allow_date_changes: true,
    allow_guest_changes: true,
    allow_room_category_changes: true,
    allow_rate_price_changes: true,
  },
  channel_manager: {
    allow_modifications: true,
    allow_cancellation: false,
    allow_date_changes: true,
    allow_guest_changes: true,
    allow_room_category_changes: true,
    allow_rate_price_changes: true,
  },
};

export const normalizeReservationManualModificationPolicy = (value) => {
  const input =
    value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return Object.fromEntries(
    RESERVATION_MODIFICATION_SOURCE_META.map(({ key }) => {
      const sourceInput =
        input[key] && typeof input[key] === "object" && !Array.isArray(input[key])
          ? input[key]
          : {};
      const defaults = DEFAULT_RESERVATION_MANUAL_MODIFICATION_POLICY[key];
      return [
        key,
        Object.fromEntries(
          Object.entries(defaults).map(([settingKey, fallback]) => [
            settingKey,
            typeof sourceInput[settingKey] === "boolean"
              ? sourceInput[settingKey]
              : fallback,
          ])
        ),
      ];
    })
  );
};

export const getReservationPolicyOriginType = (reservation) => {
  const explicit = normalizeSourceValue(
    reservation?.origin_type ??
      reservation?.source_attribution?.origin_type ??
      reservation?.source_attribution?.source_type
  );
  if (["channelmanager", "ota", "external", "externalapi"].includes(explicit)) {
    return "channel_manager";
  }
  if (["bookingengine", "motorreservas"].includes(explicit)) {
    return "booking_engine";
  }
  if (["pms", "direct", "manual", "pilcore"].includes(explicit)) {
    return "pms";
  }

  const sourceValues = [
    reservation?.origin_system,
    reservation?.external_agency_name,
    reservation?.source_system,
    reservation?.source,
  ].map(normalizeSourceValue);
  if (
    sourceValues.some((value) =>
      ["bookingengine", "pilcorebookingengine"].includes(value)
    )
  ) {
    return "booking_engine";
  }
  if (
    sourceValues.some((value) =>
      CHANNEL_MANAGER_SOURCE_MARKERS.some((marker) => value.includes(marker))
    )
  ) {
    return "channel_manager";
  }
  const hasExternalAttribution =
    reservation?.source_attribution?.has_external_source === true ||
    reservation?.source_attribution?.has_external_source === 1 ||
    String(
      reservation?.source_attribution?.has_external_source || ""
    ).toLowerCase() === "true";
  const hasExternalLocator = Boolean(
    String(
      reservation?.external_reservation_id ||
        reservation?.external_partner_locator ||
        ""
    ).trim()
  );
  const hasExternalContext = Boolean(
    String(
      reservation?.external_creator_id ||
        reservation?.external_hotel_id ||
        reservation?.external_agency_name ||
        ""
    ).trim()
  );
  if (hasExternalAttribution || (hasExternalLocator && hasExternalContext)) {
    return "channel_manager";
  }
  return "pms";
};

const ACTION_SETTING_KEYS = {
  cancellation: "allow_cancellation",
  dates: "allow_date_changes",
  guests: "allow_guest_changes",
  room_category: "allow_room_category_changes",
  rate_prices: "allow_rate_price_changes",
};

export const getReservationModificationBlock = ({
  reservation,
  policy,
  actions,
}) => {
  const normalizedPolicy =
    normalizeReservationManualModificationPolicy(policy);
  const originType = getReservationPolicyOriginType(reservation);
  const sourcePolicy = normalizedPolicy[originType];
  const requestedActions = [
    ...new Set(
      (Array.isArray(actions) ? actions : [actions]).filter(
        (action) => ACTION_SETTING_KEYS[action]
      )
    ),
  ];
  const sourceLabel =
    RESERVATION_MODIFICATION_SOURCE_META.find(({ key }) => key === originType)
      ?.label || originType;
  const deniedActions = sourcePolicy.allow_modifications
    ? requestedActions.filter(
        (action) => !sourcePolicy[ACTION_SETTING_KEYS[action]]
      )
    : requestedActions;

  if (!deniedActions.length) return null;

  const deniedLabels = deniedActions.map(
    (action) =>
      RESERVATION_MODIFICATION_ACTION_META.find(
        (item) => item.action === action
      )?.label || action
  );
  return {
    originType,
    deniedActions,
    message: sourcePolicy.allow_modifications
      ? `${deniedLabels.join(
          " and "
        )} changes are disabled for ${sourceLabel} reservations in Settings → Reservation Settings.`
      : `Manual modifications are disabled for ${sourceLabel} reservations in Settings → Reservation Settings.`,
  };
};
